import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { TEAL, AMBER, SKY, GREEN, SLATE, BORD, CARD, SUNK, DARK, MUTED, DIM, TEXT } from '../stockShared';

// Suggest a change (the director, 28 Oct 2026; migration 084): an operator or
// setter presses Suggest beside the bell; the app takes a picture of the page
// as it is, they circle the spot and say what should change, and it goes to
// the director -- who marks it and replies, and the answer comes back here
// and on their bell. Opened over the page by its own button, never in the way.

const MARK = '#f43f5e';
const MAX_W = 1600;   // the picture sent is at most this wide

export const STATUS = {
  new:       { label: 'New',       color: SLATE },
  seen:      { label: 'Seen',      color: SKY },
  done:      { label: 'Done',      color: GREEN },
  not_doing: { label: 'Not doing', color: AMBER },
};
export const statusChip = (s) => {
  const x = STATUS[s] || STATUS.new;
  return <span style={{ fontSize: 11, fontWeight: 700, color: x.color, border: `1px solid ${x.color}66`, background: `${x.color}14`, borderRadius: 6, padding: '1px 7px', whiteSpace: 'nowrap' }}>{x.label}</span>;
};
export const when = (iso) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

// The page as the person sees it: the part of it on screen, before the panel
// opens. Loaded only when asked, so it costs every other page nothing.
export async function takePicture() {
  const html2canvas = (await import('html2canvas')).default;
  return html2canvas(document.body, {
    backgroundColor: DARK, logging: false, useCORS: true,
    x: window.scrollX, y: window.scrollY, width: window.innerWidth, height: window.innerHeight,
    scale: Math.min(window.devicePixelRatio || 1, 1.5),
  });
}

// The picture with its marks, as a JPEG no wider than MAX_W.
function toJpeg(canvas) {
  return new Promise((resolve) => {
    let c = canvas;
    if (canvas.width > MAX_W) {
      c = document.createElement('canvas');
      c.width = MAX_W; c.height = Math.round(canvas.height * MAX_W / canvas.width);
      c.getContext('2d').drawImage(canvas, 0, 0, c.width, c.height);
    }
    c.toBlob(resolve, 'image/jpeg', 0.82);
  });
}

//   shot: the picture (a canvas); null when it could not be taken; undefined
//         when opened from the bell, where none was asked for
//   page: { path, title }   startOn: 'new' | 'mine'   top: px below the bar
export default function SuggestPanel({ shot, page, startOn = 'new', top, phone, onClose }) {
  const { toast } = useToast();
  const [tab, setTab] = useState(startOn);
  const [text, setText] = useState('');
  const [marks, setMarks] = useState([]);
  const [drag, setDrag] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [mine, setMine] = useState(null);
  const viewRef = useRef(null);
  const panelRef = useRef(null);

  const loadMine = useCallback(() => {
    api.get('/api/v1/suggestions/mine').then(r => setMine(r.data)).catch(() => setMine([]));
  }, []);
  useEffect(() => { loadMine(); }, [loadMine]);
  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onClose]);

  // The picture and its circles, drawn at the picture's own size.
  useEffect(() => {
    const c = viewRef.current;
    if (!c || !shot) return;
    c.width = shot.width; c.height = shot.height;
    const g = c.getContext('2d');
    g.drawImage(shot, 0, 0);
    const lw = Math.max(3, shot.width / 320);
    g.strokeStyle = MARK; g.lineWidth = lw;
    for (const m of drag ? [...marks, drag] : marks) {
      g.beginPath();
      g.ellipse((m.x0 + m.x1) / 2, (m.y0 + m.y1) / 2, Math.max(Math.abs(m.x1 - m.x0) / 2, lw * 4), Math.max(Math.abs(m.y1 - m.y0) / 2, lw * 4), 0, 0, Math.PI * 2);
      g.stroke();
    }
  }, [shot, marks, drag, tab]);

  const at = (e) => {
    const r = viewRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * shot.width / r.width, y: (e.clientY - r.top) * shot.height / r.height };
  };
  const down = (e) => { e.preventDefault(); viewRef.current.setPointerCapture(e.pointerId); const p = at(e); setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y }); };
  const move = (e) => { if (!drag) return; const p = at(e); setDrag(d => ({ ...d, x1: p.x, y1: p.y })); };
  const up = () => {
    if (!drag) return;
    // A tap rings the spot; a drag rings what it covers.
    const r = shot.width / 12;
    const m = Math.abs(drag.x1 - drag.x0) + Math.abs(drag.y1 - drag.y0) < shot.width / 80
      ? { x0: drag.x0 - r, y0: drag.y0 - r * 0.6, x1: drag.x0 + r, y1: drag.y0 + r * 0.6 } : drag;
    setMarks(ms => [...ms, m]);
    setDrag(null);
  };

  async function send() {
    if (!text.trim()) { setError('Say what should change, in a few words. The picture goes with it.'); return; }
    setSending(true); setError(null);
    try {
      const f = new FormData();
      const blob = shot && viewRef.current ? await toJpeg(viewRef.current) : null;
      if (blob) f.append('picture', blob, 'page.jpg');
      f.append('comment', text.trim());
      f.append('page_path', page.path);
      f.append('page_title', page.title || '');
      f.append('screen_w', String(window.innerWidth));
      f.append('screen_h', String(window.innerHeight));
      await api.post('/api/v1/suggestions', f, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      toast.success('Sent to the director. The answer comes back here and on your bell.');
      setText(''); setMarks([]); setTab('mine'); loadMine();
    } catch (err) {
      setError(err.response?.data?.error || 'It could not be sent just now. Check the connection and press Send again; what you wrote is kept.');
    }
    setSending(false);
  }

  async function openPicture(id) {
    try { const { data } = await api.get(`/api/v1/suggestions/${id}/picture`); window.open(data.url, '_blank', 'noopener'); }
    catch (err) { toast.error(err.response?.data?.error || 'The picture could not be opened just now.'); }
  }

  const seg = (k, label) => (
    <button type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
      style={{ background: tab === k ? CARD : 'transparent', border: 'none', boxShadow: tab === k ? `0 0 0 1px ${BORD}` : 'none', borderRadius: 7, padding: '5px 11px', fontSize: 12.5, fontWeight: 700, color: tab === k ? TEXT : DIM, cursor: 'pointer', fontFamily: 'system-ui' }}>{label}</button>
  );
  const answered = (mine || []).filter(s => s.reply || s.status !== 'new').length;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 210, pointerEvents: 'none' }}>
      <section ref={panelRef} role="dialog" aria-label="Suggest a change"
        style={{ position: 'absolute', top, right: phone ? 8 : 12, left: phone ? 8 : 'auto', width: phone ? 'auto' : 420, maxHeight: `calc(100dvh - ${top + (phone ? 76 : 12)}px)`,
          background: CARD, border: `1px solid ${BORD}`, borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden', pointerEvents: 'all', fontFamily: 'system-ui, sans-serif', color: TEXT }}>
        <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: `1px solid ${BORD}` }}>
          <i className="fa-solid fa-lightbulb" style={{ color: TEAL, fontSize: 14 }} aria-hidden="true" />
          <b style={{ fontSize: 14, flex: 1 }}>Suggest a change</b>
          <div role="tablist" style={{ display: 'inline-flex', background: SUNK, border: `1px solid ${BORD}`, borderRadius: 9, padding: 2 }}>
            {seg('new', 'New')}{seg('mine', `Yours${mine && mine.length ? ` · ${mine.length}` : ''}`)}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', color: DIM, fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
        </header>

        {tab === 'new' ? (
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
            {shot ? (
              <>
                <div style={{ fontSize: 12.5, color: DIM }}>The page as you saw it. Draw round the part you mean, or tap it.</div>
                <canvas ref={viewRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => setDrag(null)}
                  aria-label="A picture of the page. Draw on it to mark the spot."
                  style={{ width: '100%', borderRadius: 8, border: `1px solid ${BORD}`, cursor: 'crosshair', touchAction: 'none', display: 'block' }} />
                {marks.length > 0 && (
                  <button type="button" onClick={() => setMarks([])} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: DIM, fontSize: 12.5, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                    Clear the {marks.length === 1 ? 'circle' : `${marks.length} circles`}
                  </button>
                )}
              </>
            ) : shot === undefined ? (
              <div style={{ fontSize: 12.5, color: DIM, lineHeight: 1.5 }}>To send one with a picture, close this, go to the page you mean and press Suggest there.</div>
            ) : (
              <div style={{ fontSize: 12.5, color: AMBER, lineHeight: 1.5 }}>The picture of this page could not be taken. Say which part you mean; the director still sees which page it came from.</div>
            )}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 13, fontWeight: 700 }}>
              What should change?
              <textarea value={text} onChange={e => setText(e.target.value)} rows={4} maxLength={2000} autoFocus={!phone}
                placeholder="e.g. The Label line should count sheets, not pieces."
                style={{ background: DARK, border: `1px solid ${BORD}`, borderRadius: 8, color: TEXT, padding: '8px 10px', fontSize: 13.5, fontFamily: 'system-ui', resize: 'vertical', fontWeight: 400 }} />
            </label>
            <div style={{ fontSize: 12, color: MUTED }}>From: {page.title || page.path}</div>
            {error && <div role="alert" style={{ fontSize: 12.5, color: AMBER, lineHeight: 1.5 }}>{error}</div>}
            <button type="button" onClick={send} disabled={sending}
              style={{ background: TEAL, color: '#0f172a', border: 'none', borderRadius: 9, padding: '10px 14px', fontSize: 14, fontWeight: 800, cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.6 : 1, fontFamily: 'system-ui' }}>
              {sending ? 'Sending…' : 'Send to the director'}
            </button>
          </div>
        ) : (
          <div style={{ overflowY: 'auto' }}>
            {mine === null && <div style={{ padding: 16, fontSize: 13, color: DIM }}>Reading…</div>}
            {mine && !mine.length && <div style={{ padding: 16, fontSize: 13, color: DIM, lineHeight: 1.5 }}>Nothing sent yet. When you send a suggestion, the director's answer shows here.</div>}
            {mine && mine.length > 0 && <div style={{ padding: '10px 14px 4px', fontSize: 12, color: MUTED }}>{answered} of {mine.length} answered</div>}
            {(mine || []).map(s => (
              <article key={s.id} style={{ padding: '10px 14px', borderTop: `1px solid ${BORD}`, display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: MUTED }}>
                  {statusChip(s.status)}<span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.page_title || s.page_path}</span><span>{when(s.created_at)}</span>
                </div>
                <div style={{ fontSize: 13.5, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{s.comment}</div>
                {s.reply && (
                  <div style={{ background: SUNK, border: `1px solid ${BORD}`, borderRadius: 8, padding: '7px 10px', fontSize: 13, lineHeight: 1.45 }}>
                    <span style={{ color: TEAL, fontWeight: 700 }}>{s.replied_by || 'The director'}:</span> <span style={{ whiteSpace: 'pre-wrap' }}>{s.reply}</span>
                  </div>
                )}
                {s.has_picture && (
                  <button type="button" onClick={() => openPicture(s.id)} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: DIM, fontSize: 12, cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>See the picture</button>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
